import { DEFAULT_CATEGORIES } from '../features/category/defaultCategories';
import { callEdgeApi, SheetsFetchError, type ApiPayload } from './api';
import { rowsToPositions, rowsToCategories, rowsToNames, joinData, ensureAmenityCategory, type SheetsResult } from './transform';

export type { FetchErrorKind } from './api';
export { SheetsFetchError } from './api';
export type { SheetsResult } from './transform';

/**
 * Force a network fetch. SWR calls this as the fetcher; the cache is
 * managed by SWR itself (in MarketDataProvider / useVendors).
 *
 * In DEV, falls back to demo data if `/api/vendors` is unreachable so
 * dev-without-vercel still works.
 */
export async function fetchFreshData(): Promise<SheetsResult> {
  // Demo-data builds (the local Lighthouse harness, offline demos) skip the
  // network entirely and serve the deterministic demo dataset, so a build
  // without a live `/api/vendors` renders the real app instead of the error
  // overlay.
  if (import.meta.env.VITE_DEMO_DATA === '1') {
    const { getDemoData } = await import('./demo');
    return getDemoData();
  }

  let payload: ApiPayload;
  try {
    payload = await callEdgeApi();
  } catch (e) {
    if (import.meta.env.DEV && e instanceof SheetsFetchError) {
      console.error(
        '[market-map] /api/vendors unreachable in DEV — falling back to demo data.\n' +
          'If you expected real Sheets data, confirm `.env` has GOOGLE_SHEETS_API_KEY + ' +
          'GOOGLE_SHEETS_SPREADSHEET_ID and that the Vite dev API plugin is wired up in ' +
          'vite.config.ts. Underlying error:',
        e.message,
      );
      const { getDemoData } = await import('./demo');
      return getDemoData();
    }
    throw e;
  }

  const { positions, warnings: positionWarnings } = rowsToPositions(payload.positions);
  const categoriesRaw = rowsToCategories(payload.categories);
  const sheetCategories = categoriesRaw.length > 0 ? categoriesRaw : DEFAULT_CATEGORIES;
  const finalCategories = ensureAmenityCategory(sheetCategories, positions);
  const names = rowsToNames(payload.names);
  const vendors = joinData(payload.vendors, positions, finalCategories);

  return { vendors, positions, categories: finalCategories, names, positionWarnings };
}
